import React, { useEffect, useMemo, useCallback } from 'react';
import { useModal } from '../../../components/common/modal/ModalContext';
import { useProduct } from '../../../context/productContext/ProductContextProvider';
import Table from '../../../components/common/table/Table';
import { FaTrash, FaImage } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import CreateProduct from './CreateProduct';
import UpdateProductImage from './UpdateProductImage';
import ActionButtons from '../../../components/common/ActionButtons';

const ProductTable = () => {
  const {
    getProductsByPage,
    productResponse,
    clearFormData,
    totalElements,
    currentPage,
    setCurrentPage,
    pageSize,
    deleteProduct,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
    searchQuery,
  } = useProduct();
  const { openModal, closeModal } = useModal();

  const handleOpenModal = useCallback(
    (user) => {
      clearFormData();
      openModal(
        <CreateProduct
          closeModal={closeModal}
          data={user}
          isCreatedFromProposal={false}
        />,
        'Update Product',
      );
    },
    [clearFormData, openModal, closeModal],
  );

  const handleOpenImageModal = useCallback(
    (user) => {
      openModal(
        <UpdateProductImage closeModal={closeModal} data={user} />,
        'Upload Product Image',
      );
    },
    [openModal, closeModal],
  );

  const renderActionButtons = (user) => (
    <ActionButtons
      actions={[
        {
          icon: <FaTrash />,
          onClick: () => deleteProduct(user.id),
          tooltip: 'Delete Product',
          className: 'text-red-500 pr-6',
        },
        {
          icon: <MdEdit />,
          onClick: () => handleOpenModal(user),
          tooltip: 'Edit Product',
          className: 'text-gray-500 pr-6',
        },
        // {
        //   icon: <FaImage />,
        //   onClick: () => handleOpenImageModal(user),
        //   tooltip: 'Upload Image',
        //   className: 'text-gray-500',
        // },
      ]}
    />
  );

  const handleSortChange = ({ field, direction }) => {
    setSortBy(field); // Update the sortBy field
    setSortDir(direction); // Update the sort direction
    getProductsByPage(currentPage, pageSize, searchQuery, field, direction); // Fetch sorted data
  };

  const columns = useMemo(
    () => [
      { header: 'Item Code', field: 'itemCode' },
      { header: 'Product Group', field: 'productGroup.productGroupCode' },
      {
        header: 'Item Description',
        render: (data) => {
          return (
            <div>
              {data.itemDescription}
              <br />
              <small className="text-xs text-gray-500">
                {data.description2}
              </small>
            </div>
          );
        },
        field: 'itemDescription',
      },
      { header: 'Manufacturer Code', field: 'apnManuf' },
      {
        header: 'Managed Range',
        render: (data) => {
          return data.managedRange == 'M' ? 'Yes' : 'No';
        },
        field: 'managedRange',
      },
      { header: 'FLC Page No', field: 'flcPageNo' },
      { header: 'Available Online', field: 'internetFlag' },
      { header: 'Bin Location', field: 'binLoc' },
      { header: 'Unit', field: 'unit' },
      { header: 'Price 0 (Ex Gst)', field: 'price0exGst' },
      { header: 'Avg Cost', field: 'avgCost' },
      {
        header: 'GST Exempt',
        render: (data) => {
          return data.taxCode == 'E' ? 'Yes' : 'No';
        },
        field: 'taxCode',
      },
      { header: 'Special', field: 'specialFlag' },
      {
        header: 'Action',
        render: renderActionButtons,
      },
    ],
    [deleteProduct, handleOpenModal],
  );

  useEffect(() => {
    getProductsByPage();
    return () => {
      // Cleanup function to prevent memory leaks
      clearFormData();
    };
  }, [currentPage]);

  return (
    <>
      <Table
        data={productResponse}
        columns={columns}
        totalElements={totalElements}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={(newPage) => setCurrentPage(newPage)}
        keyField="id"
        onSortChange={handleSortChange} // Pass sorting handler
        sortConfig={{ field: sortBy, direction: sortDir }} // Pass current sorting state
      />
    </>
  );
};

export default ProductTable;
